import React, {useEffect, useState} from 'react';

import {BrowseCourse} from '@udemy/browse-course';
import {Tracker} from '@udemy/event-tracking';
import {FunnelLogContextProvider} from '@udemy/funnel-tracking';
import {useI18n} from '@udemy/i18n';
import {
    CertificationExamVoucherBannerWrapper,
    CertificationTopicHeader,
    CertificationTopicStatsSection,
    CertificationVoucherBundleUnitCarousel,
    VoucherUIRegion,
} from '@udemy/react-certification-preparation';
import {useExamByTopicId} from '@udemy/react-certification-vouchers';
import {
    extractCategoryAndSubcategoryItems,
    fetchRecommendationSections,
    RecommendationContextIds,
    RecommendationSourceObjectTypes,
    type RelatedCategoryItem,
} from '@udemy/react-recommendations';
import {udSentry} from '@udemy/sentry';
import {CUSTOM_EVENT_NAME_PROVIDER_BRIDGE, useUDData} from '@udemy/ud-data';

import {TopicHeaderSkeleton} from './components/topic-header-skeleton.react-component';
import {
    CERTIFICATION_TOPIC_HEADER_VARIATION,
    SPECIAL_VOUCHER_BUNDLE_DISCOUNT_PROPS,
} from './constants';
import {TopicPageViewEvent} from './events';
import {TopicProps} from './topic.react-component';
import {
    getLocalizedEnrollmentStat,
    getPageObjectIdFromTopic,
    getPageType,
    getPrimaryHeading,
} from './utils';

export const CertificationTopic = (props: Omit<TopicProps, 'udData' | 'locale'>) => {
    const {
        certification,
        certDescription,
        topic,
        topicTitle,
        presetValue,
        enrollmentStat,
        isConsumerSubsSubscriber,
        avgRating,
        numCourses,
        numExercises,
        showCertificationTopicHeaderFeature,
    } = props;

    const udData = useUDData();
    const {locale} = useI18n();

    const [relatedCategories, setRelatedCategories] = useState<RelatedCategoryItem[]>();

    useEffect(() => {
        let cancelled = false;
        fetchRecommendationSections({
            params: {
                contextId: RecommendationContextIds.TOPIC_CONTEXTUAL_HEADER,
                sourceObjects: [{type: RecommendationSourceObjectTypes.LABEL, id: topic.id}],
            },
        })
            .then((data) => {
                if (!cancelled) {
                    setRelatedCategories(extractCategoryAndSubcategoryItems(data));
                }
            })
            .catch((e) => {
                udSentry.captureException(e);
                if (!cancelled) {
                    setRelatedCategories([]);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [topic.id]);

    useEffect(() => {
        Tracker.publishEvent(new TopicPageViewEvent({topicId: getPageObjectIdFromTopic(topic)}));
    }, [topic, topic.id]);

    const marketplaceCountryId = udData.Config.marketplace_country?.id;
    useEffect(() => {
        if (!topic.id || !marketplaceCountryId) {
            return;
        }
        const timeoutId = setTimeout(() => {
            window.dispatchEvent(
                new CustomEvent(CUSTOM_EVENT_NAME_PROVIDER_BRIDGE, {
                    detail: {
                        marketplace_country: {},
                        pageContext: {entityId: topic.id.toString()},
                    },
                }),
            );
        }, 0);
        return () => clearTimeout(timeoutId);
    }, [topic.id, marketplaceCountryId]);

    const {voucherPool, examId, isAvailableForRegion} = useExamByTopicId(topic.id);
    const hasVoucher =
        !!voucherPool && !!examId && isAvailableForRegion && voucherPool.hasAvailableVouchers;

    const hasPracticeExam = (props.certificationBundles ?? []).some((bundle) =>
        (bundle.contents ?? []).some(
            (course) => (course as BrowseCourse).is_practice_test_course === true,
        ),
    );

    if (!certification) {
        return null;
    }

    if (props.isExperimentConfigsLoading) {
        return <TopicHeaderSkeleton />;
    }

    const certificationTopicHeaderVariation =
        showCertificationTopicHeaderFeature?.configuration?.variation;

    if (
        (certificationTopicHeaderVariation !== CERTIFICATION_TOPIC_HEADER_VARIATION.VARIANT_A &&
            certificationTopicHeaderVariation !== CERTIFICATION_TOPIC_HEADER_VARIATION.VARIANT_B) ||
        !certDescription
    ) {
        return null;
    }

    const pageType = getPageType(udData, isConsumerSubsSubscriber);
    const primaryHeading = getPrimaryHeading({topic, topicTitle, presetValue, pageType});
    const localizedEnrollmentStat = getLocalizedEnrollmentStat(enrollmentStat, locale);

    const heroImage = topic.images.heroImage
        ? {
              url: topic.images.heroImage.url,
              highDensityUrl: topic.images.heroImage.highDensityUrl,
          }
        : {url: certification.image.id};
    const badgeImage = topic.images.badgeImage
        ? {
              url: topic.images.badgeImage.url,
              highDensityUrl: topic.images.badgeImage.highDensityUrl,
          }
        : undefined;

    return (
        <>
            <div className="component-margin ud-container">
                <CertificationTopicHeader
                    name={primaryHeading}
                    topicDescription={certDescription}
                    images={{heroImage, badgeImage}}
                    relatedCategories={relatedCategories?.map((item) => ({
                        title: item.title,
                        url: item.url,
                    }))}
                    stats={{
                        averageRating: avgRating,
                        numLearners: localizedEnrollmentStat,
                        numPracticeExercises: numExercises,
                        numCourses: numCourses,
                    }}
                    hasVoucher={hasVoucher}
                    hasPracticeExam={hasPracticeExam}
                    variation={certificationTopicHeaderVariation}
                />
            </div>
            <div className={'component-margin'}>
                <CertificationTopicStatsSection />
            </div>
            <div className="component-margin ud-container">
                <FunnelLogContextProvider pageType="topic">
                    <CertificationVoucherBundleUnitCarousel
                        title={gettext('Get Certified with Top Instructors')}
                        subtitle={gettext(
                            'Explore curated bundles from expert instructors. Browse to compare teaching styles and prep strategies.',
                        )}
                        units={props.certificationBundles.map((bundle) => ({
                            bundle: bundle,
                            showCommonInstructor: true,
                            ...SPECIAL_VOUCHER_BUNDLE_DISCOUNT_PROPS,
                        }))}
                    />
                </FunnelLogContextProvider>
            </div>
            <div className="component-margin ud-container">
                <CertificationExamVoucherBannerWrapper
                    topicId={topic.id}
                    theme="dark"
                    layout="horizontal"
                    isTopicBanner
                    uiRegion={
                        props.isTopicVoucherModuleAboveBundle?.configuration
                            ?.isTopicVoucherModuleAbovebundle
                            ? ('above_course_bundle' as VoucherUIRegion)
                            : ('below_course_bundle' as VoucherUIRegion)
                    }
                    showAddToCart={false}
                />
            </div>
        </>
    );
};
